import { defineStore } from "pinia";
import api from "@/api/api";
import { initializePusher, disconnectPusher } from "@/api/pusher";

export const useAuthStore = defineStore("auth", {
    state: () => ({
        token: null,
        user: null,
        activeWorkspace: null,
    }),

    actions: {
        login(token, user) {
            this.token = token;
            this.user = typeof user === "string" ? JSON.parse(user) : user;

            localStorage.setItem("token", token);
            localStorage.setItem("user", JSON.stringify(this.user));

            this.setDefaultWorkspaceIfMissing();
            initializePusher(this.user.uuid);
        },

        async logout() {
            disconnectPusher();
            this.token = null;
            this.user = null;
            this.activeWorkspace = null;
            this.workspaceMemberships = {};
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("activeWorkspace");
        },

        addWorkspaceLocal(ws) {
            if (!ws || !ws.uuid) return;
            const name = ws.name || `Workspace ${ws.uuid.slice(0, 6)}`;

            const current = (this.user?.workspaces) || {};
            const next = { ...current, [ws.uuid]: { name } };

            this.user = { ...(this.user || {}), workspaces: next };
            localStorage.setItem("user", JSON.stringify(this.user));

            if (!this.activeWorkspace) {
                this.setActiveWorkspace(ws.uuid);
            }
        },

        /**
         * Get workspace name by uuid, supporting both old format (string) and new format ({ name }).
         */
        getWorkspaceName(uuid) {
            const entry = this.user?.workspaces?.[uuid];
            if (!entry) return null;
            return typeof entry === 'object' ? entry.name : entry;
        },

        getToken() {
            return this.token || localStorage.getItem("token");
        },

        loadToken() {
            this.token = localStorage.getItem("token");
        },

        checkSession() {
            const storedToken = localStorage.getItem("token");
            const storedUser = localStorage.getItem("user");
            const storedWorkspace = localStorage.getItem("activeWorkspace");

            if (storedToken && storedUser) {
                this.token = storedToken;
                this.user = JSON.parse(storedUser);
                this.activeWorkspace = storedWorkspace || null;
                this.setDefaultWorkspaceIfMissing();
                    initializePusher(this.user.uuid);
            }
        },

        // Re-fetch the user from the BE (which live-syncs workspaces from SSO).
        // Lets the app pick up workspaces added in SSO without a fresh login.
        async refreshMe() {
            if (!this.getToken()) return;
            try {
                const { data } = await api.get("/api/me");
                if (data && data.uuid) {
                    this.user = data;
                    localStorage.setItem("user", JSON.stringify(data));
                    this.setDefaultWorkspaceIfMissing();
                        }
            } catch (e) {
                // Keep the cached user on a transient failure.
            }
        },

        getUser() {
            if (typeof this.user === "string") {
                try {
                    this.user = JSON.parse(this.user);
                } catch {
                    this.user = null;
                }
            }
            return this.user;
        },

        setActiveWorkspace(uuid) {
            this.activeWorkspace = uuid || null;
            if (uuid) localStorage.setItem("activeWorkspace", uuid);
            else localStorage.removeItem("activeWorkspace");
        },

        getActiveWorkspace() {
            if (!this.getToken()) return null; // brak sesji
            if (!this.activeWorkspace) {
                const stored = localStorage.getItem("activeWorkspace");
                if (stored) {
                    this.activeWorkspace = stored;
                } else if (this.user?.workspaces) {
                    this.setDefaultWorkspaceIfMissing();
                }
            }
            return this.activeWorkspace;
        },

        hasActiveWorkspace() {
            return !!this.getActiveWorkspace();
        },

        isAdminOfActiveWorkspace() {
            const ws = this.getActiveWorkspace();
            const map = this.user?.workspaces || {};
            // If user is in the workspace, they have full access
            return !!ws && Object.prototype.hasOwnProperty.call(map, ws);
        },

        setDefaultWorkspaceIfMissing() {
            if (!this.user || !this.user.workspaces) return;

            if (!this.activeWorkspace) {
                const firstWorkspace = Object.keys(this.user.workspaces)[0];
                if (firstWorkspace) {
                    this.activeWorkspace = firstWorkspace;
                    localStorage.setItem("activeWorkspace", firstWorkspace);
                }
            }
        },

        isSsoEnabled() {
            // Require both — redirectToSso() bails without a client_id, which would
            // dead-end the guard on a blank page.
            return !!(process.env.VUE_APP_SSO_URL && process.env.VUE_APP_SSO_CLIENT_ID);
        },

        redirectToSso(emailHint = null) {
            const ssoUrl = process.env.VUE_APP_SSO_URL;
            const clientId = process.env.VUE_APP_SSO_CLIENT_ID;
            const redirectUri = process.env.VUE_APP_SSO_REDIRECT_URI;

            if (!ssoUrl || !clientId) {
                // SSO not configured - cannot redirect, avoid reload loop
                console.warn("[auth] SSO not configured (VUE_APP_SSO_URL / VUE_APP_SSO_CLIENT_ID missing)");
                return;
            }

            // Generate PKCE code_verifier and code_challenge
            const codeVerifier = generateCodeVerifier();
            const codeChallenge = generateCodeChallenge(codeVerifier);

            sessionStorage.setItem("sso_code_verifier", codeVerifier);

            codeChallenge.then((challenge) => {
                sessionStorage.setItem("sso_code_challenge", challenge);

                const params = new URLSearchParams({
                    client_id: clientId,
                    redirect_uri: redirectUri,
                    response_type: "code",
                    scope: "",
                    code_challenge: challenge,
                    code_challenge_method: "S256",
                });

                if (emailHint) {
                    params.set("email_hint", btoa(emailHint));
                }

                window.location.href = `${ssoUrl}/oauth/authorize?${params.toString()}`;
            });
        }
    },
});

// PKCE helpers
function generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64URLEncode(array);
}

async function generateCodeChallenge(verifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return base64URLEncode(new Uint8Array(digest));
}

function base64URLEncode(buffer) {
    let str = "";
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) {
        str += String.fromCharCode(bytes[i]);
    }
    return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
